import { useFormatter, useTranslations } from 'next-intl';
import { DAY_MS, MINUTE_MS } from '@/constants/time';
import { useFormatMonthDayYear } from './useFormatMonthDayYear';

/**
 * Outputs the relative time of a date in the format "1 year ago"
 *
 * ## Past ##
 * Within the last minute            Just now
 * Within 59 minutes                x minutes ago
 * 60 minutes ago                   1 hour ago
 * 1 hour ago                       x hours ago
 * x hours ago                      x hours ago
 * 1 day ago                        1 day ago
 * 2 days ago < 7 days              x days ago
 * 7 days ago                       1 week ago
 * > 7 days ago                     x weeks ago
 * 1 month ago                      1 month ago
 * > 1 month ago                    x months ago
 * 1 year ago                       1 year ago
 * > 1 year ago                     x years ago
 *
 * ## Future ##
 * Within the next few seconds      Shortly
 * In the next minute               In 1 minute
 * In the next 60 minutes           In x minutes
 * In 60 minutes                    In 1 hour
 * In x hours                       In x hours
 * In 1 day (by date, not hours)    Tomorrow
 * In 2 to 7 days                   In x days
 * In 7 days                        In 1 week
 * In > 7 days                      Thu, Jan 17, 2024
 *
 * @see [Design system](https://www.figma.com/design/ulUeUVSNgLMAYaeH0nJxCI/Den-Components?node-id=4753-3935&m=dev)
 */
export function useFormatRelativeTime() {
  const formatter = useFormatter();
  const t = useTranslations('common.datetime');
  const formatFullDate = useFormatMonthDayYear();

  return (date: Date, fromDate?: Date) => {
    const now = fromDate ?? new Date();

    const diff = date.getTime() - now.getTime();
    if (0 <= diff && diff < MINUTE_MS) {
      return t('shortly');
    }

    if (-1 * MINUTE_MS < diff && diff < 0) {
      return t('justNow');
    }

    if (8 * DAY_MS <= diff) {
      return formatFullDate(date);
    }

    // Only when the date is the next calendar day, return "Tomorrow"
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) {
      return t('tomorrow');
    }

    return formatter.relativeTime(date, {
      now,
      style: 'long',
    });
  };
}
